import React, { useState, useRef, useEffect } from 'react';
import { Layout, Dropdown, Button, MenuProps, Typography, Space, Avatar } from 'antd';
import { UserOutlined, LogoutOutlined, InfoCircleOutlined, SettingOutlined, ToolOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import GlobalTour from './Tour';
import Cookies from 'js-cookie';

const { Header } = Layout;
const { Text } = Typography;

const AppHeader: React.FC = () => {
  const location = useLocation();
  const { keycloak } = useKeycloak();

  // Get user data from Keycloak ID token
  const userInfo = {
    username: keycloak.idTokenParsed?.preferred_username || keycloak.idTokenParsed?.given_name || 'Guest',
    firstName: keycloak.idTokenParsed?.given_name || '',
    lastName: keycloak.idTokenParsed?.family_name || '',
    email: keycloak.idTokenParsed?.email || '',
    fullName:
      `${keycloak.idTokenParsed?.given_name || ''} ${keycloak.idTokenParsed?.family_name || ''}`.trim() || 'Guest User',
  };

  const isAdmin = keycloak.hasRealmRole('admin');
  const tourRef = useRef<{ restartTour: () => void }>(null);
  const [isTourEnabled, setIsTourEnabled] = useState(false);

  const toggleTour = () => {
    if (isTourEnabled) {
      setIsTourEnabled(false);
    } else {
      setIsTourEnabled(true);
      tourRef.current?.restartTour();
    }
  };

  const handleTourComplete = () => {
    setIsTourEnabled(false);
  };

  const handleTourSkip = () => {
    setIsTourEnabled(false);
  };

  const handleSignOut = () => {
    Cookies.remove('caseStudy');
    keycloak.logout();
  };

  // New handler for Keycloak account management
  const handleManageAccount = () => {
    // Check if Keycloak is initialized and we can create the URL
    if (keycloak && keycloak.authenticated) {
      const accountUrl = keycloak.createAccountUrl();
      if (accountUrl) {
        window.open(accountUrl, '_blank'); // Open the account management page in a new tab
      }
    }
  };

  const handleCaseStudyChange = (caseStudy: string) => {
    Cookies.set('caseStudy', caseStudy);
    window.location.reload();
  };

  const caseStudyMenuItems: MenuProps['items'] = [
    {
      key: 'barcelona',
      label: 'Barcelona',
      onClick: () => handleCaseStudyChange('barcelona'),
    },
    {
      key: 'cyprus',
      label: 'Cyprus',
      onClick: () => handleCaseStudyChange('cyprus'),
    },
    {
      key: 'greece',
      label: 'Greece',
      onClick: () => handleCaseStudyChange('greece'),
    },
    {
      key: 'pnr_narbonnaise',
      label: 'Pnr Narbonnaise',
      onClick: () => handleCaseStudyChange('pnr_narbonnaise'),
    },
  ];

  // Improved user menu with better styling and organization
  const userMenuItems: MenuProps['items'] = [
    // User Profile Section
    {
      key: 'profile',
      label: (
        <div
          style={{
            padding: '8px 0',
            borderBottom: '1px solid #f0f0f0',
            marginBottom: '8px',
          }}
        >
          <Space direction='vertical' size={2} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Avatar size={32} icon={<UserOutlined />} style={{ backgroundColor: 'var(--primary-color)' }} />
              <Space direction='vertical' size={0}>
                <Text strong style={{ fontSize: '14px', lineHeight: '20px' }}>
                  {userInfo.fullName}
                </Text>
                <Text type='secondary' style={{ fontSize: '12px', lineHeight: '16px' }}>
                  {userInfo.email}
                </Text>
              </Space>
            </div>
          </Space>
        </div>
      ),
      disabled: true,
    },

    // Keycloak Account Management Button
    {
      key: 'manageAccount',
      icon: <ToolOutlined />, // Using ToolOutlined for account settings/management
      label: 'Manage Account',
      onClick: handleManageAccount,
    },

    {
      type: 'divider' as const,
    },

    // Admin Section (conditionally rendered)
    ...(isAdmin
      ? [
        {
          key: 'admin-section',
          type: 'group' as const,
          label: 'Administration',
          children: [
            {
              key: 'caseStudySelection',
              icon: <SettingOutlined />,
              label: (
                <Dropdown
                  menu={{ items: caseStudyMenuItems }}
                  placement='bottomRight'
                  trigger={['click']}
                  overlayStyle={{ minWidth: '160px' }}
                >
                  <Button
                    type='text'
                    style={{
                      padding: 0,
                      height: 'auto',
                      color: 'inherit',
                      textAlign: 'left',
                    }}
                  >
                    Select Case Study
                  </Button>
                </Dropdown>
              ),
            },
          ],
        },
        {
          type: 'divider' as const,
        },
      ]
      : []),

    // Actions Section
    {
      key: 'signout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      onClick: handleSignOut,
      style: { color: '#ff4d4f' }, // Red color for sign out
    },
  ];

  useEffect(() => {
    setIsTourEnabled(false);
  }, [location]);

  return (
    <>
      <Header
        style={{
          padding: 0,
          background: 'white',
          height: 50,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingRight: '16px',
          boxShadow: '0 1px 4px rgba(0,21,41,.08)', // Subtle shadow for depth
        }}
      >
        <Text strong style={{ marginLeft: 16, fontSize: '16px' }}>
          Welcome, {userInfo.firstName || userInfo.username}
        </Text>

        <Space size='middle'>
          <Button
            type={isTourEnabled ? 'default' : 'primary'}
            style={{
              backgroundColor: isTourEnabled ? '#ff7875' : '#52c41a',
              borderColor: isTourEnabled ? '#ff7875' : '#52c41a',
              color: 'white',
              fontWeight: 500,
            }}
            icon={<InfoCircleOutlined />}
            onClick={toggleTour}
          >
            {isTourEnabled ? 'Turn Off Help' : 'Help'}
          </Button>

          <Dropdown
            menu={{ items: userMenuItems }}
            placement='bottomRight'
            overlayStyle={{ minWidth: '280px' }}
            trigger={['click']}
          >
            <Button
              style={{
                display: 'flex',
                alignItems: 'center',
                background: 'none',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '4px 12px',
                height: '32px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--primary-color)';
                e.currentTarget.style.backgroundColor = 'rgb(240, 255, 240)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#d9d9d9';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Space size={8}>
                <Avatar size={20} icon={<UserOutlined />} style={{ backgroundColor: 'var(--primary-color)' }} />
                <Text style={{ fontWeight: 500 }}>{userInfo.firstName || userInfo.username}</Text>
              </Space>
            </Button>
          </Dropdown>
        </Space>
      </Header>

      <GlobalTour ref={tourRef} isEnabled={isTourEnabled} onComplete={handleTourComplete} onSkip={handleTourSkip} />
    </>
  );
};

export default AppHeader;